import type { ConditionGroup, FieldDef, FieldOption, FieldType, SectionDef } from '../forms/types.js';

/**
 * Small helpers for authoring source templates with stable ids.
 * Ids are derived deterministically from the section prefix + semantic key and must never
 * change once a template version is published.
 */

export function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'item';
}

export type OptionSpec =
  | string
  | { label: string; key?: string; exclusive?: boolean; opensDetail?: boolean };

export function options(fieldId: string, specs: OptionSpec[]): FieldOption[] {
  const seen = new Set<string>();
  return specs.map((s) => {
    const spec = typeof s === 'string' ? { label: s } : s;
    let key = spec.key ?? slug(spec.label);
    while (seen.has(key)) key = `${key}_2`;
    seen.add(key);
    const o: FieldOption = { id: `${fieldId}__${key}`, key, label: spec.label };
    if (spec.exclusive) o.exclusive = true;
    if (spec.opensDetail) o.opensDetail = true;
    return o;
  });
}

export type FieldExtra = Omit<Partial<FieldDef>, 'id' | 'key' | 'type' | 'label' | 'options'> & {
  options?: OptionSpec[];
};

export class SectionBuilder {
  readonly fields: FieldDef[] = [];
  constructor(
    readonly prefix: string,
    readonly meta: { number: string; title: string; key: string; description?: string; instructions?: string[]; visibleWhen?: ConditionGroup },
  ) {}

  id(key: string) {
    return `${this.prefix}_${key}`;
  }

  make(key: string, type: FieldType, label: string, extra: FieldExtra = {}): FieldDef {
    const id = this.id(key);
    const { options: optSpecs, ...rest } = extra;
    const f: FieldDef = { id, key, type, label, sourceRef: this.meta.number, ...rest };
    if (optSpecs) f.options = options(id, optSpecs);
    return f;
  }

  add(key: string, type: FieldType, label: string, extra: FieldExtra = {}): FieldDef {
    const f = this.make(key, type, label, extra);
    this.fields.push(f);
    return f;
  }

  /** Repeater child ids are namespaced by the repeater id. */
  child(repeaterKey: string, key: string, type: FieldType, label: string, extra: FieldExtra = {}): FieldDef {
    const id = `${this.prefix}_${repeaterKey}__${key}`;
    const { options: optSpecs, ...rest } = extra;
    const f: FieldDef = { id, key, type, label, sourceRef: this.meta.number, ...rest };
    if (optSpecs) f.options = options(id, optSpecs);
    return f;
  }

  info(key: string, text: string, label = 'Note'): FieldDef {
    return this.add(key, 'info', label, { text });
  }

  optionId(fieldKey: string, optionKey: string): string {
    return `${this.id(fieldKey)}__${optionKey}`;
  }

  build(): SectionDef {
    const s: SectionDef = {
      id: this.prefix,
      key: this.meta.key,
      number: this.meta.number,
      title: this.meta.title,
      fields: this.fields,
    };
    if (this.meta.description) s.description = this.meta.description;
    if (this.meta.instructions) s.instructions = this.meta.instructions;
    if (this.meta.visibleWhen) s.visibleWhen = this.meta.visibleWhen;
    return s;
  }
}

export const ACCESS_STATUS_OPTIONS: OptionSpec[] = [
  { label: 'Pending', key: 'pending' },
  { label: 'Granted', key: 'granted' },
];

export const NO_PASSWORDS_NOTICE =
  'Never share passwords, recovery codes or one-time passcodes (OTPs). Grant access by adding the requested user with the appropriate role/permission inside the platform.';
